// 181 A.5 (ruling 4) — OUTLOOK'S 25 CATEGORY COLOURS, ONE TABLE, TWO READERS.
//
// Phase 33 (Change C) wrote this palette inside SettingsView for its two colour
// pickers. 181 gives the e-mail row three dots that wear the SAME colours the
// firm's Outlook categories wear (green saved · yellow billed · purple
// attachments), so the row and Settings must read ONE table — a second copy is
// how a preset renumbers on one screen and not the other. Moved here verbatim;
// the ids are the Graph `categoryColor` enum strings, the hex an approximate
// swatch (the real colour is whatever Outlook renders), preset→name verified
// against the Graph docs (outlookCategory) when it was written.

import { CT } from "./tokens";

export interface OutlookPresetColor {
  id: string;
  name: string;
  hex: string;
}

export const OUTLOOK_PRESET_COLORS: OutlookPresetColor[] = [
  { id: "preset0", name: "Red", hex: CT.c_E81123 },
  { id: "preset1", name: "Orange", hex: CT.c_F7630C },
  { id: "preset2", name: "Brown", hex: CT.c_8E562E },
  { id: "preset3", name: "Yellow", hex: CT.c_FCE100 },
  { id: "preset4", name: "Green", hex: CT.c_16C60C },
  { id: "preset5", name: "Teal", hex: CT.c_00B7C3 },
  { id: "preset6", name: "Olive", hex: CT.c_8CBD18 },
  { id: "preset7", name: "Blue", hex: CT.c_0078D7 },
  { id: "preset8", name: "Purple", hex: CT.c_8764B8 },
  { id: "preset9", name: "Cranberry", hex: CT.c_C30052 },
  { id: "preset10", name: "Steel", hex: CT.c_5D7079 },
  { id: "preset11", name: "Dark steel", hex: CT.c_34515E },
  { id: "preset12", name: "Gray", hex: CT.c_939393 },
  { id: "preset13", name: "Dark gray", hex: CT.c_4D4D4D },
  { id: "preset14", name: "Black", hex: CT.c_1B1B1B },
  { id: "preset15", name: "Dark red", hex: CT.c_750B1C },
  { id: "preset16", name: "Dark orange", hex: CT.c_A74109 },
  { id: "preset17", name: "Dark brown", hex: CT.c_4C2C18 },
  { id: "preset18", name: "Dark yellow", hex: CT.c_C19C00 },
  { id: "preset19", name: "Dark green", hex: CT.c_0B6A0B },
  { id: "preset20", name: "Dark teal", hex: CT.c_006666 },
  { id: "preset21", name: "Dark olive", hex: CT.c_556B2F },
  { id: "preset22", name: "Dark blue", hex: CT.c_0A2E5C },
  { id: "preset23", name: "Dark purple", hex: CT.c_401B6C },
  { id: "preset24", name: "Dark cranberry", hex: CT.c_6E0F3C },
];

/** The three categories' Setting keys and their defaults — the same three
 *  `server/src/lib/m365.js` CATEGORY_COLOR_SETTING declares (registered =
 *  yellow, saved = green, attachments = purple), spelled once on this side. */
export const OUTLOOK_CATEGORY_SETTINGS = {
  billed: { key: "emailBilledCategoryColor", def: "preset3" },
  saved: { key: "emailSavedCategoryColor", def: "preset4" },
  attachments: { key: "emailAttachmentsCategoryColor", def: "preset8" },
} as const;

/** The swatch for a preset id off the wire; a value that is not a known
 *  preset resolves to the category's default, never to nothing. */
export function presetHex(id: unknown, fallbackId: string): string {
  const want = typeof id === "string" ? id.toLowerCase() : fallbackId;
  const hit = OUTLOOK_PRESET_COLORS.find((c) => c.id === want) ?? OUTLOOK_PRESET_COLORS.find((c) => c.id === fallbackId);
  return (hit ?? OUTLOOK_PRESET_COLORS[0]).hex;
}
